import {
  selectAllFields,
  selectAnySignedSigntures,
  selectExpirationData,
  selectFieldsWithNa,
  selectLastUpdatedAtData,
  selectMarkUnchangedFields,
  selectUnsavedFieldsWithNa,
  selectUnsavedValues,
  selectValues,
  updateMarkUnchangedFields,
  updateUnsavedFieldWithNa,
  updateUnsavedValue,
} from 'components/QuestionnaireRequestAutomated/processor/slices/formInstanceSlice'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import QuestionField from './field'
import { Checkbox, RadioCheckbox } from './optionsField'

function LastUpdatedInfo({ date }) {
  return (
    <div className="dome-d-flex dome-align-end">
      <div className="dome-d-flex dome-align-center dome-justify-between dome-gap6">
        <i className="fa-thin fa-circle-exclamation dome-color-aqua" />
        <span className="dome-color-dark-grey" style={{ minWidth: 170 }}>
          last updated: {date}
        </span>
      </div>
    </div>
  )
}

export const useQuestionState = (questionKey) => {
  const allFields = useSelector(selectAllFields)
  const values = useSelector(selectValues)
  const fieldsWithNa = useSelector(selectFieldsWithNa)
  const unsavedValues = useSelector(selectUnsavedValues)
  const unsavedFieldsWithNa = useSelector(selectUnsavedFieldsWithNa)
  const expirationData = useSelector(selectExpirationData)
  const lastUpdatedAtData = useSelector(selectLastUpdatedAtData)
  const markUnchangedFields = useSelector(selectMarkUnchangedFields)
  const isAnySignedSignatures = useSelector(selectAnySignedSigntures)

  // Derive state for the specific question
  const state = useMemo(() => {
    const field = allFields[questionKey]
    const currentDate = new Date().setHours(0, 0, 0, 0)

    const value = unsavedValues[questionKey] === undefined ? values[questionKey] : unsavedValues[questionKey]
    const isNa = unsavedFieldsWithNa[questionKey]?.na ?? fieldsWithNa[questionKey]?.na
    const isMarkedUnchanged = markUnchangedFields[questionKey] || false

    const isQuestionExpired =
      expirationData[questionKey] && new Date(expirationData[questionKey]).setHours(0, 0, 0, 0) < currentDate

    const isSaved =
      !isQuestionExpired &&
      unsavedValues[questionKey] === undefined &&
      unsavedFieldsWithNa[questionKey] === undefined &&
      (fieldsWithNa[questionKey]?.na || values[questionKey] !== undefined)

    const lastUpdatedAt = lastUpdatedAtData[questionKey]
      ? new Date(lastUpdatedAtData[questionKey]).toLocaleDateString()
      : null

    return {
      field,
      value,
      isNa,
      isMarkedUnchanged,
      isQuestionExpired,
      isSaved,
      isAnySignedSignatures,
      lastUpdatedAt,
    }
  }, [
    questionKey,
    allFields,
    values,
    fieldsWithNa,
    unsavedValues,
    unsavedFieldsWithNa,
    expirationData,
    lastUpdatedAtData,
    markUnchangedFields,
    isAnySignedSignatures,
  ])

  return state
}

const Question = ({ question }) => {
  const dispatch = useDispatch()
  const key = question?.question_attributes?.field
  const values = useSelector(selectValues)

  const { field, value, isNa, isMarkedUnchanged, isQuestionExpired, isSaved, isAnySignedSignatures, lastUpdatedAt } =
    useQuestionState(key)

  const handleMarkUnchanged = useCallback(
    (e) => {
      const isChecked = e.currentTarget.checked

      if (isChecked) {
        if (isNa) {
          dispatch(updateUnsavedFieldWithNa({ id: key, value: { na: isChecked } }))
        } else {
          dispatch(updateUnsavedValue({ id: key, value: values[key] }))
        }
      }

      dispatch(updateMarkUnchangedFields({ [key]: isChecked }))
    },
    [dispatch, isNa, key, values]
  )

  const handleValueChange = useCallback(
    (e) => {
      if (e[key] === 'n/a') {
        dispatch(updateUnsavedFieldWithNa({ id: key, value: { na: true } }))
      } else {
        dispatch(updateUnsavedValue({ id: key, value: e[key] }))
      }
    },
    [dispatch, key]
  )

  const handleNaToggle = useCallback(() => {
    dispatch(updateUnsavedFieldWithNa({ id: key, value: { na: !isNa } }))
  }, [dispatch, key, isNa])

  if (!field) return null // Gracefully handle cases where the field data isn't ready

  const isDisabled = isAnySignedSignatures
  const opacity = isSaved || isDisabled ? 0.4 : 1

  return (
    <>
      <div className="dome-d-flex dome-justify-between dome-gap12" style={{ paddingRight: '20px', opacity: opacity }}>
        <span className="dome-p1 dome-color-darkest-grey">{field.label}</span>
        {isQuestionExpired && lastUpdatedAt && <LastUpdatedInfo date={lastUpdatedAt} />}
      </div>

      <div className="dome-d-flex dome-gap6">
        {field.form_field.type === 'select' || field.form_field.type === 'options' ? (
          <QuestionField
            id={key}
            isOpacity={isSaved}
            options={field.options}
            field={field.form_field}
            disabled={isDisabled}
            value={isNa ? 'n/a' : value}
            onChange={handleValueChange}
            endIcon={
              <div className="dome-d-flex dome-align-center dome-gap6">
                {isQuestionExpired && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      name={`question-${question.id}`}
                      checked={isMarkedUnchanged}
                      onChange={handleMarkUnchanged}
                      inputClasses="square-gray-checkbox"
                      label="mark unchanged"
                      disabled={isDisabled}
                    />
                  </div>
                )}
                {field.form_field.type === 'select' && (
                  <i className="fa-light fa-solid fa-chevron-down dome-color-med-grey"></i>
                )}
              </div>
            }
          />
        ) : (
          <div className="dome-d-flex input-field-container" style={{ gap: 10, padding: '3px 6px', opacity: opacity }}>
            <QuestionField
              id={key}
              isNa={isNa}
              value={isNa ? '' : value}
              field={field.form_field}
              className="dome-full-width"
              disabled={isDisabled}
              onChange={(e) => dispatch(updateUnsavedValue({ id: key, value: e[key] }))}
            />
            <div className="dome-d-flex dome-align-center dome-gap6" style={{ padding: '3px 6px' }}>
              {isQuestionExpired && (
                <Checkbox
                  label="mark unchanged"
                  inputClasses="square-gray-checkbox"
                  name={`question-${question.id}`}
                  checked={isMarkedUnchanged}
                  onChange={handleMarkUnchanged}
                  disabled={isDisabled}
                />
              )}
              <RadioCheckbox checked={isNa} onChange={handleNaToggle} label="n/a" disabled={isDisabled} />
            </div>
          </div>
        )}

        <div style={{ minWidth: 14, opacity: opacity }} className="dome-d-flex dome-align-center">
          {(value || isNa) && !isQuestionExpired && <i className="fa-solid fa-circle-check dome-color-sage"></i>}
        </div>
      </div>
    </>
  )
}

export default function AllFields({ questions, activeQuestion }) {
  const questionRefs = useRef({})
  useEffect(() => {
    if (activeQuestion && questionRefs.current[activeQuestion]) {
      questionRefs.current[activeQuestion].scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [activeQuestion])

  return (
    <div className="questionnaire-request-interstitial-field-main-questions">
      <ul
        className="dome-d-flex dome-flex-column dome-gap36"
        style={{ listStyle: 'none', padding: '0 0 0 12px', margin: 0 }}
      >
        {questions?.map((question) => (
          <li
            ref={(el) => {
              if (el) questionRefs.current[question.question_attributes.field] = el
              else delete questionRefs.current[question.question_attributes.field]
            }}
            key={question.id}
            className="auto-questionnaire-input-field dome-d-flex dome-flex-column dome-gap6"
          >
            <Question question={question} />
          </li>
        ))}
      </ul>
    </div>
  )
}
